import * as React from 'react';
import * as PropTypes from 'prop-types';

import { Keys } from 'locales/keys';
import { ErrorCode } from 'modules/error/enums/ErrorCode';
import { Localizer } from 'modules/main/components/Localizer';
import { DataFetchState } from 'modules/pub-docs/enums/DataFetchState';
import { isNil } from 'modules/main/services/lodash-extended';

import { FileViewer } from 'modules/pub-docs/components/FileViewer';
import { PoweredByDms } from 'modules/pub-docs/components/PoweredByDms';
import { TPublicDoc } from 'modules/pub-docs/models/TPublicDoc';
import { getOriginalDocumentUrl } from 'modules/pub-docs/services/pubDocServices';
import { Column } from './Column';
import { LoadFailureMessageBox } from '../LoadFailureMessageBox/LoadFailureMessageBox';

const visuallyHiddenStyle: React.CSSProperties = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  width: '1px',
  whiteSpace: 'nowrap',
};

type TListViewProps = {
  headerIsLoading: boolean;
  selectDocument: (document: TPublicDoc) => void;

  documents?: TPublicDoc[];
  errorCode?: ErrorCode;
  hasError?: boolean;
  onRetry?: () => void;
  selectedDocument?: TPublicDoc;
  siteName?: string;
  siteKey?: string;
};

/**
 * TODO: This was componentized for testing purposes. Try testing for the existence of
 * `PublicDocs.EmptyMessage` instead. e.g.
 *
 * window.localStrings['PublicDocs.EmptyMessage'] = 'blah blah';
 *
 * shallow(<PubDocsListView dataFetchState="empty" />);
 *
 * expect 'blah blah` text to exist.
 */
const EmptyMessage: React.FC<{}> = () => (
  <span className="pubDocsListView_empty">
    <Localizer translation={Keys.PublicDocs.EmptyMessage} />
  </span>
);

function useListViewActiveContent(selectedDocument: TPublicDoc) {
  const [activeContent, setActiveContent] = React.useState<'list' | 'fileViewer'>('list');

  React.useEffect(() => {
    if (activeContent === 'fileViewer' && isNil(selectedDocument)) {
      setActiveContent('list');
    }

    if (activeContent === 'list' && !isNil(selectedDocument)) {
      setActiveContent('fileViewer');
    }
  }, [activeContent, selectedDocument]);

  return activeContent;
}

const ListView: React.FC<TListViewProps> = (props: TListViewProps) => {
  const {
    headerIsLoading,
    selectDocument,
    documents,
    errorCode,
    hasError,
    onRetry,
    selectedDocument,
    siteKey,
    siteName,
  } = props;

  const activeContent = useListViewActiveContent(selectedDocument);
  const [fileFetchState, setFileFetchState] = React.useState<DataFetchState>(DataFetchState.Empty);
  const [announcement, setAnnouncement] = React.useState<string>('');

  React.useEffect(() => {
    if (activeContent === 'fileViewer' && selectedDocument?.name) {
      setAnnouncement(`${selectedDocument.name} has been loaded`);
    }
  }, [activeContent, selectedDocument]);

  let viewContent = null;

  if (activeContent === 'fileViewer' && !isNil(selectedDocument)) {
    const { breadcrumbs, id, name, publicOriginalDownload, publicUrl } = selectedDocument;
    const trail = (breadcrumbs && breadcrumbs[0] && breadcrumbs[0].trail) || [];
    const originalDocumentUrl =
      siteKey && id && publicOriginalDownload ? getOriginalDocumentUrl(siteKey, id) : undefined;

    viewContent = (
      <FileViewer
        breadcrumbTrail={trail}
        disablePointerEvents={false}
        documentName={name}
        documentUrl={publicUrl}
        fileFetchState={fileFetchState}
        headerIsLoading={headerIsLoading}
        originalDocumentUrl={originalDocumentUrl}
        setFileFetchState={setFileFetchState}
        siteKey={siteKey}
        siteName={siteName}
      />
    );
  }

  if (activeContent === 'list') {
    viewContent = (
      <main className="pubDocsListView">
        <h1 className="pubDocsListView_header page-header-font ellipse">
          {!hasError && siteName ? (
            siteName
          ) : (
            <Localizer translation={Keys.PublicDocs.PageHeader} />
          )}
        </h1>

        <div className="pubDocsListView_body">
          {hasError ? (
            <LoadFailureMessageBox errorCode={errorCode} onRetry={onRetry} />
          ) : documents.length === 0 ? (
            <EmptyMessage />
          ) : (
            <Column documents={documents} selectDocument={(doc) => selectDocument(doc)} />
          )}
        </div>
        <div className="pubDocsListView_footer">
          <div className="pubDocsListView_footer_legalLinksContainer">
            <a
              href="https://www.powerdms.com/terms-of-use"
              target="_blank"
              className="pubDocsListView_footer_legalLink"
            >
              Terms
            </a>
            <a
              href="https://www.powerdms.com/privacy-policy"
              target="_blank"
              className="pubDocsListView_footer_legalLink"
            >
              Privacy
            </a>
          </div>

          <PoweredByDms />
        </div>
      </main>
    );
  }

  if (viewContent === null) {
    return null;
  }

  return (
    <>
      <div aria-live="polite" style={visuallyHiddenStyle}>
        {announcement}
      </div>
      {viewContent}
    </>
  );
};

ListView.propTypes = {
  siteName: PropTypes.string,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  documents: PropTypes.array,
};

ListView.defaultProps = {
  hasError: false,
  onRetry: () => {},
  documents: [],
};

export { ListView };
